/**
 * Runtime configuration for the SPA.
 *
 * In production the SPA is served from S3 + CloudFront and can fetch a
 * `/config.json` artifact emitted by the deploy pipeline. For the
 * prototype, configuration is read from `window.__TCVS_CONFIG__` populated
 * by a `<script>` tag in `index.html` (or `process.env.*` at build time
 * if a bundler is wired up later).
 *
 * The SPA does not embed any secrets — only the Cognito client id, the
 * Hosted UI domain, and the API base URL.
 */

import type { CognitoConfig } from './auth/cognito';

export interface SpaConfig {
  cognito: CognitoConfig;
  apiBaseUrl: string;
  helpApiBaseUrl: string;
  environment: SpaEnvironmentConfig;
}

export interface SpaEnvironmentConfig {
  stage: string;
  account: string;
  region: string;
}

declare global {
  interface Window {
    __TCVS_CONFIG__?: Omit<SpaConfig, 'environment' | 'helpApiBaseUrl'> & {
      helpApiBaseUrl?: string;
      environment?: SpaEnvironmentConfig;
    };
  }
}

const DEFAULT_CONFIG: SpaConfig = {
  cognito: {
    hostedUiDomain: 'https://example.auth.us-east-1.amazoncognito.com',
    clientId: 'placeholder-client-id',
    redirectUri: 'http://localhost:5173/',
    scopes: ['openid', 'profile'],
  },
  apiBaseUrl: 'http://localhost:3000',
  helpApiBaseUrl: 'http://localhost:3000',
  environment: {
    stage: 'local',
    account: 'local',
    region: 'local',
  },
};

/** Read the active SPA configuration. Falls back to placeholders. */
export function loadConfig(): SpaConfig {
  if (typeof window !== 'undefined' && window.__TCVS_CONFIG__ !== undefined) {
    const configured = window.__TCVS_CONFIG__;
    return {
      ...configured,
      helpApiBaseUrl: configured.helpApiBaseUrl ?? configured.apiBaseUrl,
      environment: configured.environment ?? {
        stage: 'unknown',
        account: 'unknown',
        region: 'unknown',
      },
    };
  }
  return DEFAULT_CONFIG;
}
